import { useState, useMemo, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEntriesNeedingRole, useAdminUpdateEntryRole, useBulkSetRole } from '../../hooks/useAdminEntries';
import { useProjects } from '../../hooks/useProjects';
import { useAdminUsers } from '../../hooks/useAdmin';
import { Spinner } from '../../components/common/Spinner';
import { Modal } from '../../components/common/Modal';
import { Button } from '../../components/common/Button';
import { readableEntryAdminError, type EntryNeedingRole } from '../../services/api/adminEntries';
// VERIFIED 2026-05-26: project roles are exposed via `useProjectRoles` in
// frontend/src/hooks/useLeads.ts:71 — calls `fetchProjectRoles()` in
// frontend/src/services/api/leads.ts:142, which hits `GET /project-roles` (backend route).
// ProjectRole shape: { id: number; name: string; code: string | null; referenceCount?: number }
// The hook returns TanStack { data: { data: ProjectRole[] } } (TanStack wrapper + API envelope).
import { useProjectRoles } from '../../hooks/useLeads';
import { formatDisplayDate } from '../../utils/format';

function formatDate(s: string | null): string {
  if (!s) return '—';
  return formatDisplayDate(s);
}

interface RoleOption {
  id: number;
  name: string;
}

interface RowProps {
  entry: EntryNeedingRole;
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
  onFixRole: (entryId: number, roleId: number) => void;
  isSaving: boolean;
  roles: RoleOption[];
}

const EntryRow = memo(function EntryRow({
  entry,
  isSelected,
  onToggleSelect,
  onFixRole,
  isSaving,
  roles,
}: RowProps) {
  const [pickerRoleId, setPickerRoleId] = useState<number | ''>('');
  return (
    <tr className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(entry.id)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-3 text-sm">{entry.project?.name ?? '—'}</td>
      <td className="px-4 py-3 text-sm">{entry.userId ?? '—'}</td>
      <td className="px-4 py-3 text-sm">{formatDate(entry.date)}</td>
      <td className="px-4 py-3 text-sm text-right">{entry.hours.toFixed(1)} h</td>
      <td className="px-4 py-3 text-sm">{entry.description ?? '—'}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={pickerRoleId}
            onChange={(e) =>
              setPickerRoleId(e.target.value === '' ? '' : Number(e.target.value))
            }
            disabled={isSaving}
            aria-label="Select role"
          >
            <option value="">Pick a role…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            size="sm"
            disabled={pickerRoleId === '' || isSaving}
            onClick={() => pickerRoleId !== '' && onFixRole(entry.id, pickerRoleId)}
          >
            Fix
          </Button>
        </div>
      </td>
    </tr>
  );
});

export default function EntriesNeedingRolePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFilter = searchParams.get('projectIds') ?? '';
  const userFilter = searchParams.get('userIds') ?? '';
  const fromMonth = searchParams.get('fromMonth') ?? '';
  const toMonth = searchParams.get('toMonth') ?? '';
  const page = Number.parseInt(searchParams.get('page') ?? '0', 10);
  const pageSize = 50;

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkRoleId, setBulkRoleId] = useState<number | ''>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
    setSelectedIds(new Set());
  };

  const projectIds = useMemo(
    () =>
      projectFilter
        ? projectFilter
            .split(',')
            .map(Number)
            .filter((n) => Number.isFinite(n))
        : undefined,
    [projectFilter],
  );

  const userIds = useMemo(
    () =>
      userFilter
        ? userFilter
            .split(',')
            .map(Number)
            .filter((n) => Number.isFinite(n))
        : undefined,
    [userFilter],
  );

  const query = useEntriesNeedingRole({
    projectIds,
    userIds,
    fromMonth: fromMonth || undefined,
    toMonth: toMonth || undefined,
    page,
    pageSize,
  });

  const updateMutation = useAdminUpdateEntryRole();
  const bulkMutation = useBulkSetRole();

  const projectsQuery = useProjects();
  const usersQuery = useAdminUsers();
  const rolesQuery = useProjectRoles();

  // ProjectRole[] from the API envelope: { data: { data: ProjectRole[] } }
  const roles: RoleOption[] =
    rolesQuery.data?.data?.map((r: { id: number; name: string }) => ({ id: r.id, name: r.name })) ?? [];

  const rows = query.data?.data ?? [];
  const pagination = query.data?.pagination;

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (rows.every((r) => selectedIds.has(r.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  };

  const handleFixRole = async (entryId: number, roleId: number) => {
    setErrorMessage(null);
    try {
      await updateMutation.mutateAsync({ id: entryId, input: { roleId } });
    } catch (err) {
      setErrorMessage(readableEntryAdminError(err));
    }
  };

  const handleBulkSubmit = async () => {
    if (bulkRoleId === '' || selectedIds.size === 0) return;
    setErrorMessage(null);
    try {
      await bulkMutation.mutateAsync({
        entryIds: Array.from(selectedIds),
        roleId: bulkRoleId,
      });
      setSelectedIds(new Set());
      setShowBulkModal(false);
      setBulkRoleId('');
    } catch (err) {
      setErrorMessage(readableEntryAdminError(err));
    }
  };

  const allRowsSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold">Entries needing role</h1>
        <p className="text-sm text-gray-600 mt-1">
          SLA Support entries with no role assigned. These are silently excluded from SLA package
          overage billing — fix the role to include them in the next invoice generation.
        </p>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Project</label>
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={projectFilter}
            onChange={(e) => setFilter('projectIds', e.target.value)}
            aria-label="Filter by project"
          >
            <option value="">All projects</option>
            {projectsQuery.data?.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">User</label>
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={userFilter}
            onChange={(e) => setFilter('userIds', e.target.value)}
            aria-label="Filter by user"
          >
            <option value="">All users</option>
            {usersQuery.data?.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">From month</label>
          <MonthYearPicker
            value={fromMonth}
            onChange={(v) => setFilter('fromMonth', v)}
            ariaLabel="From month"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">To month</label>
          <MonthYearPicker
            value={toMonth}
            onChange={(v) => setFilter('toMonth', v)}
            ariaLabel="To month"
          />
        </div>
      </div>

      {/* Bulk action bar — visible when >= 1 row selected */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 flex items-center justify-between">
          <span className="text-sm">{selectedIds.size} selected</span>
          <Button variant="primary" size="sm" onClick={() => setShowBulkModal(true)}>
            Set role for selected
          </Button>
        </div>
      )}

      {/* Error banner */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 mb-3 text-sm">
          {errorMessage}
        </div>
      )}

      {/* Table */}
      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={allRowsSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider">Project</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider">User</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider">Date</th>
                <th className="px-4 py-2 text-right text-xs uppercase tracking-wider">Hours</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider">Description</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider">Fix role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-sm text-gray-500 py-6">
                    No orphan-role entries match the current filters.
                  </td>
                </tr>
              ) : (
                rows.map((entry) => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    isSelected={selectedIds.has(entry.id)}
                    onToggleSelect={toggleSelect}
                    onFixRole={handleFixRole}
                    isSaving={updateMutation.isPending}
                    roles={roles}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Page {pagination.page + 1} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => setFilter('page', String(page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page + 1 >= pagination.totalPages}
              onClick={() => setFilter('page', String(page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Bulk confirm modal — uses Modal directly because the role picker needs JSX children */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => {
          setShowBulkModal(false);
          setBulkRoleId('');
          setErrorMessage(null);
        }}
        title="Set role for selected entries"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBulkModal(false);
                setBulkRoleId('');
                setErrorMessage(null);
              }}
              disabled={bulkMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkSubmit}
              disabled={bulkRoleId === '' || bulkMutation.isPending}
              isLoading={bulkMutation.isPending}
            >
              Set role
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-gray-700">
          Selected: <strong>{selectedIds.size}</strong> entries
        </p>
        <label className="block text-sm text-gray-700 mb-1" htmlFor="bulk-role-select">
          Role
        </label>
        <select
          id="bulk-role-select"
          className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
          value={bulkRoleId}
          onChange={(e) => setBulkRoleId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Pick a role…</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-2">
          Each updated entry gets an audit row. Entries attached to issued invoices are blocked.
        </p>
        {errorMessage && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        )}
      </Modal>
    </div>
  );
}

// Petra UAT 2026-05-27 — native <input type="month"> shows month names in
// browser locale (Czech for Petra). Replaced with two selects holding
// English month names so the filter stays English-only per memory
// feedback_english_only_ui. Value remains YYYY-MM under the hood so the
// backend contract is unchanged.
const MONTH_NAMES: ReadonlyArray<string> = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface MonthYearPickerProps {
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
}

function MonthYearPicker({ value, onChange, ariaLabel }: MonthYearPickerProps) {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear + 1; y >= currentYear - 6; y--) years.push(y);
  const [yearPart, monthPart] = value ? value.split('-') : ['', ''];
  const handleChange = (yp: string, mp: string) => {
    if (yp && mp) onChange(`${yp}-${mp}`);
    else onChange('');
  };
  return (
    <div className="flex gap-1">
      <select
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        value={monthPart ?? ''}
        onChange={(e) => handleChange(yearPart ?? '', e.target.value)}
        aria-label={`${ariaLabel} (month)`}
      >
        <option value="">Any month</option>
        {MONTH_NAMES.map((name, idx) => {
          const v = String(idx + 1).padStart(2, '0');
          return (
            <option key={v} value={v}>
              {name}
            </option>
          );
        })}
      </select>
      <select
        className="border border-gray-300 rounded px-2 py-1 text-sm"
        value={yearPart ?? ''}
        onChange={(e) => handleChange(e.target.value, monthPart ?? '')}
        aria-label={`${ariaLabel} (year)`}
      >
        <option value="">Any year</option>
        {years.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}
